import _ from "lodash";
import { Sentry } from "services/sentry";
import { print } from "graphql";
import { USER_ACCESS_BLOCKED_NETWORK_ERROR } from "Constants/stringConstants";

export const codes = {
  UNAUTHORIZED: {
    id: "UNAUTHORIZED",
  },
  UNAVAILABLE_RESOURCE: {
    id: "UNAVAILABLE_RESOURCE",
  },
  BAD_USER_INPUT: {
    id: "BAD_USER_INPUT",
  },
  GRAPHQL_VALIDATION_FAILED: {
    id: "GRAPHQL_VALIDATION_FAILED",
  },
  MIGRATION_IN_PROGRESS_ERROR: {
    id: "MIGRATION_IN_PROGRESS_ERROR",
  },
  MIGRATION_COMPLETED_ERROR: {
    id: "MIGRATION_COMPLETED_ERROR",
  },
};

const isGraphQLBadInputError = e => {
  if (e?.extensions?.code == codes.BAD_USER_INPUT.id) {
    return true;
  }
  return false;
};

const isGraphQLValidationError = e => {
  if (e?.extensions?.code == codes.GRAPHQL_VALIDATION_FAILED.id) {
    return true;
  }
  return false;
};

export const isUnavailableResourceError = e => {
  if (_.get(e, "extensions.code") == codes.UNAVAILABLE_RESOURCE.id) {
    return true;
  }
  return false;
};

export const isUnauthorizedError = e => {
  if (_.get(e, "extensions.code") == codes.UNAUTHORIZED.id) {
    return true;
  }
  return false;
};

export const isUserBlocked = e => {
  return _.get(e, "result.message") === USER_ACCESS_BLOCKED_NETWORK_ERROR;
};

export const isMigrationInProgressError = e => {
  return _.get(e, "result.id") === codes.MIGRATION_IN_PROGRESS_ERROR.id;
};

export const isMigrationCompletedError = e => {
  return _.get(e, "result.id") === codes.MIGRATION_COMPLETED_ERROR.id;
};

const shouldReportGraphQLError = e => {
  if (isGraphQLBadInputError(e)) {
    return true;
  } else if (isGraphQLValidationError(e)) {
    return true;
  }
  return false;
};

/**
 * @description process&capture&send errors to sentry
 */
export const onErrorLinkSentry = errorHandler => {
  const { graphQLErrors, networkError, operation } = errorHandler;
  const {
    extensions: { route = "NO_ROUTE" } = {},
    operationName = "NOP",
    variables = {},
  } = operation;

  const query = _.trim(_.get(operation, "query.loc.source.body", ""));

  graphQLErrors?.forEach(e => {
    if (shouldReportGraphQLError(e)) {
      const { extensions: { code = "NO_CODE" } = {}, message = null } = e;
      /**
       * @description console.logs with huge data will increase breadcrumbs size-
       * which will exceed  the sentry event payload size leading to
       * sentry discarding the event from sending it to server so
       * it is required to clearBreadcrumbs.
       */
      Sentry.getCurrentHub().getScope().clearBreadcrumbs();

      Sentry.withScope(scope => {
        scope.setFingerprint([message, operationName, code]);
        scope.setLevel("error");

        Sentry.captureMessage(message, {
          tags: {
            route,
            operationName,
            code,
          },
          extra: {
            query,
            variables: JSON.stringify(variables),
          },
        });
      });
    }
  });
};

const _stringify = obj => {
  return JSON.stringify(obj, undefined, 2);
};

const addBreadcrumb = ({ category, message, level = "info", data }) => {
  try {
    if (message && typeof message !== "string") {
      message = `${_stringify(message)}`;
    }
  } catch (err) {
    //don't do anything
  }
  Sentry.addBreadcrumb({ category, message, level, data });
};

export const logGraphqlBreadcrumb = ({ data }) => {
  try {
    const { operation, networkError, graphQLErrors } = data || {};
    const { query, operationName } = operation || {};
    let { response } = data || {};
    let { variables } = operation || {};
    const compliedQuery = print(query);
    Sentry.withScope(scope => {
      try {
        scope.setTags({
          operationName,
        });
        // Log query and variables as extras (make sure to strip out sensitive data!)
        scope.setExtras({
          query: compliedQuery,
          variables: _stringify(variables),
        });

        response = _stringify(response, undefined, 2);

        variables = _stringify(variables, undefined, 2);

        const context = _.invoke(_.get(data, "operation", {}), "getContext");

        const headers = _.get(context, "response.headers", {});

        const networkErrorHeaders = _.get(networkError, "response.headers", {});

        addBreadcrumb({
          category: "gql headers",
          message: `${_stringify(
            { operationName, Headers: { ...headers, ...networkErrorHeaders } },
            undefined,
            2
          )}`,
        });

        addBreadcrumb({
          category: "gql response",
          data: { operationName, response },
        });

        if (networkError && !_.isEmpty(networkError)) {
          const message =
            _.get(networkError, "result.message", null) ||
            _.get(networkError, "result.bodyText", null) ||
            _.get(networkError, "bodyText", null) ||
            _.get(networkError, "response.statusText", null);

          const statusCode = _.get(networkError, "statusCode", "");

          scope.setExtra("network-error", _stringify(networkError));

          scope.setExtra("status-code", statusCode);
          addBreadcrumb({
            category: "gql network error",
            data: { operationName, error: _stringify(networkError) },
          });
          if (!_.isEmpty(message)) {
            _.set(networkError, "message", `${message} ${statusCode}`);

            const error = new Error(message);
            _.set(error, "name", `NetworkError-${message}`);
            // eslint-disable-next-line @toddle-edu/common/no-sentry-capture-exception
            Sentry.captureException(error, {
              tags: { logger: "NetworkError" },
            });
          }
        }

        onErrorLinkSentry(data);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log(err);
  }
};
